import React from 'react';
import { Link } from 'react-router-dom';
import { PunchClock } from '@models/punchClock.model';
import { PunchSessionAdjustment } from '@models/punchSessionAdjustment.model';
import {
  GeofenceCategoryIcon,
  DEFAULT_GEOFENCE_COLOR,
} from '@utils/geofenceCategoryIcons';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';

// Green ENTER / red EXIT pills — the same colour cue the per-punch rows used,
// kept on the session card to orient the eye to each end at a glance.
const EnterBadge = (
  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full uppercase tracking-wide bg-green-100 text-green-700">
    <LoginIcon sx={{ fontSize: 11 }} />
    Enter
  </span>
);
const ExitBadge = (
  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full uppercase tracking-wide bg-red-100 text-red-700">
    <LogoutIcon sx={{ fontSize: 11 }} />
    Exit
  </span>
);

/**
 * The subset of a punch Session this card renders. The drawer's full `Session`
 * is structurally assignable to this — the card owns the shape it needs and
 * stays decoupled from the drawer's pairing internals.
 */
export interface PunchSessionCardSession {
  /** Effective entry: the correction if edited, else the raw ENTER time. */
  from: Date;
  /** Raw ENTER time — the pre-edit reference (struck through when corrected). */
  rawFrom: Date;
  /** Effective exit: the correction if edited, else the raw exit, else now. */
  to: Date;
  /** Raw paired exit (null for an open / no-exit-row session). */
  rawTo: Date | null;
  isOpen: boolean;
  /** The ENTER punch anchoring this session — source of project/geofence. */
  enterPunch: PunchClock;
  locationName: string;
  locationAddress: string | null;
}

interface PunchSessionCardProps {
  session: PunchSessionCardSession;
  /** The session's correction, if any — drives the "Edited" chip + struck raw. */
  adjustment: PunchSessionAdjustment | null;
  /** Whether the current user may correct hours (mirrors backend gate). */
  canEdit: boolean;
  /** Opens the edit modal for this session. */
  onEdit: () => void;
}

// "8:02 AM" — the compact wall-clock label used on both ends of the session.
const formatTime = (d: Date): string =>
  d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const formatMinutes = (mins: number): string => {
  const total = Math.max(0, Math.floor(mins));
  if (total < 1) return '0m';
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const formatEditedWhen = (value: string | Date): string =>
  new Date(value).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const editorLabel = (adjustment: PunchSessionAdjustment): string => {
  const user = adjustment.lastEditedByUser;
  const name = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
  return name || user?.email || 'a manager';
};

// A time is "materially changed" only when it moved by more than a minute —
// avoids a strikethrough for sub-minute rounding noise. Mirrors PunchHistoryRow.
const MATERIAL_CHANGE_MS = 60_000;

export const PunchSessionCard: React.FC<PunchSessionCardProps> = ({
  session,
  adjustment,
  canEdit,
  onEdit,
}) => {
  const { from, rawFrom, to, rawTo, isOpen, enterPunch, locationName, locationAddress } =
    session;

  const entryChanged =
    Math.abs(from.getTime() - rawFrom.getTime()) > MATERIAL_CHANGE_MS;
  const exitChanged =
    rawTo != null && Math.abs(to.getTime() - rawTo.getTime()) > MATERIAL_CHANGE_MS;
  // Exit was manually set for a session that never had a real EXIT punch.
  const exitSet = !isOpen && rawTo == null && !!adjustment;

  const durationMinutes = (to.getTime() - from.getTime()) / 60000;

  const project = enterPunch.project;
  const geofence = enterPunch.geofence;
  const geofenceColor =
    geofence?.category?.color || geofence?.color || DEFAULT_GEOFENCE_COLOR;

  return (
    <div className="bg-white rounded-lg ring-1 ring-gray-900/5 shadow-sm p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {/* Session span: [Enter] entry → [Exit] exit · duration */}
          <div className="flex items-center gap-2 flex-wrap">
            {EnterBadge}
            {entryChanged ? (
              <span className="text-sm">
                <span className="text-gray-400 line-through mr-1">
                  {formatTime(rawFrom)}
                </span>
                <span className="text-blue-700 font-semibold">
                  {formatTime(from)}
                </span>
              </span>
            ) : (
              <span className="text-sm font-medium text-gray-900">
                {formatTime(from)}
              </span>
            )}

            <span className="text-gray-400">→</span>

            {!isOpen && ExitBadge}

            {isOpen ? (
              <span className="text-sm font-semibold text-emerald-600">
                In progress
              </span>
            ) : exitChanged ? (
              <span className="text-sm">
                <span className="text-gray-400 line-through mr-1">
                  {formatTime(rawTo as Date)}
                </span>
                <span className="text-blue-700 font-semibold">
                  {formatTime(to)}
                </span>
              </span>
            ) : exitSet ? (
              <span className="text-sm font-semibold text-blue-700">
                {formatTime(to)}
              </span>
            ) : (
              <span className="text-sm font-medium text-gray-900">
                {formatTime(to)}
              </span>
            )}

            {!isOpen && (
              <>
                <span className="text-gray-300">·</span>
                <span className="text-sm font-semibold text-gray-900">
                  {formatMinutes(durationMinutes)}
                </span>
              </>
            )}

            {adjustment && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full uppercase tracking-wide bg-blue-100 text-blue-700">
                <HistoryToggleOffIcon sx={{ fontSize: 11 }} />
                Edited
              </span>
            )}
          </div>

          {/* Location: project link (else geofence name) + address. */}
          <div className="mt-1.5 flex items-center gap-1 text-xs text-gray-600 min-w-0">
            <LocationOnOutlinedIcon
              sx={{ fontSize: 14 }}
              className="text-gray-400 flex-shrink-0"
            />
            {project?.name && project?.projectId ? (
              <Link
                to={`/projects/${project.projectId}`}
                className="font-medium text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1 truncate"
              >
                {project.name}
                <LaunchIcon sx={{ fontSize: 12 }} className="flex-shrink-0" />
              </Link>
            ) : (
              <span className="font-medium truncate">{locationName}</span>
            )}
          </div>
          {locationAddress && (
            <p className="mt-0.5 text-[11px] text-gray-500 truncate pl-5">
              {locationAddress}
            </p>
          )}

          {adjustment && (
            <p className="mt-1 text-[11px] text-blue-700/80">
              Edited by {editorLabel(adjustment)} ·{' '}
              {formatEditedWhen(adjustment.updatedAt)}
            </p>
          )}
        </div>

        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          {geofence && (
            <div
              className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium"
              style={{
                backgroundColor: `${geofenceColor}1A`,
                color: geofenceColor,
              }}
            >
              <GeofenceCategoryIcon
                iconKey={geofence.category?.icon}
                sx={{ fontSize: 12 }}
              />
              <span className="truncate max-w-[140px]">{geofence.name}</span>
            </div>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={onEdit}
              title={isOpen ? 'Close session' : 'Edit session hours'}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
            >
              <EditOutlinedIcon sx={{ fontSize: 14 }} />
              {isOpen ? 'Close session' : 'Edit hours'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PunchSessionCard;
